import { useMemo } from 'react';
import { roundToTwoDecimals } from '../../../../../helpers/Units';
import { useRetrieveActiveProject } from '../../../../../hooks/project/useRetrieveActiveProject';
import { useRetrieveActivePlan } from '../plan-details/useRetrieveActivePlan';
import { useRetrieveActivePlanDrivers } from '../plan-details/useRetrieveActivePlanDrivers';

export interface UseRetrieveAnalyticsPlanRoutesResponse {
  totalKM: number;
  totalMinutes: number;
  totalStops: number;
  averageMinutesPerStop: number;
  stopReports: {
    completed: number;
    pending: number;
    incomplete: number;
    canceled: number;
  };
}

export const useRetrieveAnalyticsPlanRoutes = (): UseRetrieveAnalyticsPlanRoutesResponse => {
  const { data: activeProject } = useRetrieveActiveProject();
  const { data: planDrivers } = useRetrieveActivePlanDrivers();
  const { data: activePlan } = useRetrieveActivePlan();

  const { totalKM, totalMinutes, totalStops, averageMinutesPerStop, stopReports } =
    useMemo<UseRetrieveAnalyticsPlanRoutesResponse>(() => {
      let totalKM = 0;
      let totalMinutes = 0;
      let totalStops = 0;
      let averageMinutesPerStop = 0;
      let stopReports = {
        completed: 0,
        pending: 0,
        incomplete: 0,
        canceled: 0,
      };

      if (!activePlan || !activeProject || !planDrivers) {
        return {
          totalKM,
          totalMinutes,
          totalStops,
          averageMinutesPerStop,
          stopReports,
        };
      }

      totalKM = roundToTwoDecimals(planDrivers.reduce((prev, driver) => prev + driver.getTotalDistanceKM(), 0));
      totalMinutes = roundToTwoDecimals(planDrivers.reduce((prev, driver) => prev + driver.getTotalMinutes(), 0));
      totalStops = planDrivers.reduce((prev, driver) => prev + driver.stops.length, 0);
      averageMinutesPerStop = totalStops > 0 ? roundToTwoDecimals(totalMinutes / totalStops) : 0;

      stopReports = planDrivers.reduce(
        (prev, driver) => {
          const driverResult = driver.stops.reduce(
            (prev, stop) => ({
              completed: prev.completed + (stop.status === `completed` ? 1 : 0),
              pending: prev.pending + (stop.status === `pending` ? 1 : 0),
              incomplete: prev.incomplete + (stop.status === `incomplete` ? 1 : 0),
              canceled: prev.canceled + (stop.status === `canceled` ? 1 : 0),
            }),
            {
              completed: 0,
              pending: 0,
              incomplete: 0,
              canceled: 0,
            }
          );
          return {
            completed: prev.completed + driverResult.completed,
            pending: prev.pending + driverResult.pending,
            incomplete: prev.incomplete + driverResult.incomplete,
            canceled: prev.canceled + driverResult.canceled,
          };
        },
        {
          completed: 0,
          pending: 0,
          incomplete: 0,
          canceled: 0,
        }
      );
      return {
        totalKM,
        totalMinutes,
        totalStops,
        averageMinutesPerStop,
        stopReports,
      };
    }, [activePlan, planDrivers, activeProject]);

  return { totalKM, totalMinutes, totalStops, averageMinutesPerStop, stopReports };
};
